import log from 'loglevel'
import axios, { type AxiosHeaders } from 'axios'
import {
  filter,
  flatten,
  groupBy,
  isNil,
  keys,
  map,
  max,
  omit,
  range,
  reduce,
  size,
  slice,
  sortBy,
  split,
  uniq,
  uniqBy
} from 'lodash/fp'

import { url, getCustomHeaders } from 'components/DataProvider/functions'
import { assign } from 'lodash'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import xlsx from 'xlsx-populate'
import { json2xlsx } from '@mzc-watchover/json2xlsx'
import { type ConfigProps } from 'components/Dashboard'
import {
  type EditData,
  type Mapping
} from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'
import { format, parseISO } from 'date-fns/fp'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const doDownload = async (
  partner: Partial<Partner> | undefined
): Promise<number | undefined> => {
  if (isNil(url)) return undefined
  const customHeaders = getCustomHeaders(
    {},
    !isNil(partner) ? partner.id : undefined
  ) as AxiosHeaders | undefined
  if (isNil(customHeaders)) return undefined
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  if (!isNil(partner)) {
    assign(headers, { 'X-Partner': partner.id })
  }
  const { status } = await axios.post(
    `${url}/users/download`,
    {},
    {
      headers
    }
  )
  return status
}
export const doCognitoDownload = async (): Promise<number | undefined> => {
  if (isNil(url)) return undefined
  const headers = getCustomHeaders({}, undefined) as AxiosHeaders | undefined
  if (isNil(headers)) return undefined
  const { status } = await axios.post(
    `${url}/cognito/users/download`,
    {},
    {
      headers: { Authorization: headers.get('Authorization') }
    }
  )
  return status
}
export const doSolarAccountsDownload = async (
  partner: Partial<Partner>
): Promise<{
  mappings: Array<Record<string, string | null | undefined>> | undefined
  accounts:
    | Array<{
        name: string
        email: string
        group1: string
        group2: string
        status: string
      }>
    | undefined
}> => {
  if (isNil(url)) return { mappings: undefined, accounts: undefined }
  const customHeaders = getCustomHeaders({}, partner.id) as
    | AxiosHeaders
    | undefined
  if (isNil(customHeaders)) return { mappings: undefined, accounts: undefined }
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  assign(headers, { 'X-Partner': partner.id })
  const { status, data } = await axios.get(`${url}/solarAccounts`, {
    headers
  })
  return status === 200 ? data : { mappings: undefined, accounts: undefined }
}
export const doMappingsDownload = async (
  partner: Partial<Partner>
): Promise<{
  mappings: Mapping[] | undefined
  accounts: Array<{ name: string }> | undefined
  ownerAccounts: Array<{ name: string }> | undefined
  format: string | undefined
}> => {
  if (isNil(url))
    return {
      mappings: undefined,
      accounts: undefined,
      ownerAccounts: undefined,
      format: undefined
    }
  const customHeaders = getCustomHeaders({}, partner.id) as
    | AxiosHeaders
    | undefined
  if (isNil(customHeaders))
    return {
      mappings: undefined,
      accounts: undefined,
      ownerAccounts: undefined,
      format: undefined
    }
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  assign(headers, { 'X-Partner': partner.id })
  const { status, data } = await axios.get(`${url}/mappings`, {
    headers
  })
  return status === 200
    ? data
    : {
        mappings: undefined,
        accounts: undefined,
        ownerAccounts: undefined,
        format: undefined
      }
}
export const setMappingsValidation = async (
  data: Uint8Array,
  mappings: Array<Record<string, string>>,
  accounts: Array<{ name: string }>,
  ownerAaccounts: Array<{ name: string }>
): Promise<Uint8Array> => {
  const workbook = await xlsx.fromDataAsync(data)
  const sheet = workbook.sheet('拠点一覧')
  for (let i = 0; i < size(mappings); i++) {
    sheet.cell(`F${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: '担当者',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: '担当者',
      operator: 'String',
      formula1: `担当者一覧!$A$2:$A$${size(accounts) + 2}`,
      formula2: 'String'
    })
    sheet.cell(`G${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: 'オーナー',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: 'オーナー',
      operator: 'String',
      formula1: `オーナー一覧!$A$2:$A$${size(ownerAaccounts) + 2}`,
      formula2: 'String'
    })
  }
  // eslint-disable-next-line @typescript-eslint/return-await
  return await workbook.outputAsync({ type: 'uint8array' })
}
export const setGroupValidation = async (
  data: Uint8Array,
  accounts: Array<{
    name: string
    email: string
    group1: string
    group2: string
    status: string
  }>
): Promise<Uint8Array> => {
  const workbook = await xlsx.fromDataAsync(data)
  const sheet = workbook.sheet('Sheet1')
  const endRow1 = workbook
    .sheet('役割1')
    .usedRange()
    ?.endCell()
    .rowNumber() as number
  const endRow2 = workbook
    .sheet('役割2')
    .usedRange()
    ?.endCell()
    .rowNumber() as number
  for (let i = 0; i < size(accounts); i++) {
    sheet.cell(`C${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: '役割1',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: '役割1',
      operator: 'String',
      formula1: `役割1!$A$1:$A$${endRow1}`,
      formula2: 'String'
    })
    sheet.cell(`D${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: '役割2',
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: '役割2',
      operator: 'String',
      formula1: `役割2!$A$1:$A$${endRow2}`,
      formula2: 'String'
    })
  }
  // eslint-disable-next-line @typescript-eslint/return-await
  return await workbook.outputAsync({ type: 'uint8array' })
}
export const setAdditionalValidations = async (
  data: Uint8Array,
  mappings: Array<{
    area: string | null | undefined
    office: string | null | undefined
  }>,
  accounts: number,
  title: { area: string; office: string }
): Promise<Uint8Array | undefined> => {
  const { allArea, allOffices } = reduce(
    (
      result: {
        allArea: Array<{ area: string }>
        allOffices: Array<{ area: string; office: string }>
      },
      item: {
        area: string | null | undefined
        office: string | null | undefined
      }
    ) => {
      const { area, office } = item
      if (!isNil(area)) {
        result.allArea.push({ area })
        if (!isNil(office)) result.allOffices.push({ area, office })
      }
      return result
    },
    { allArea: [], allOffices: [] }
  )(mappings)
  const areaItems = sortBy<{ area: string }>('area')(
    uniqBy<{ area: string }>('area')(allArea)
  )
  const xlsxData = await json2xlsx(areaItems, undefined, {
    sheet: '一覧1',
    template: data,
    noHeader: true,
    startRow: 1,
    startColumn: 1,
    noFreezePanes: true
  })
  if (isNil(xlsxData)) return undefined
  const workbook = await xlsx.fromDataAsync(xlsxData)
  workbook.addSheet('一覧2')
  const officeItems = groupBy<{ area: string; office: string }>('area')(
    allOffices
  )
  for (let i = 0; i < size(areaItems); i++) {
    if (isNil(areaItems[i])) continue
    const { area } = areaItems[i] as { area: string }
    const offices = uniq(
      map((item: { area: string; office: string }) => item.office)(
        officeItems[area]
      )
    )
    if (isNil(offices)) continue
    const range = workbook.sheet('一覧2').range(1, i + 1, size(offices), i + 1)
    range.value(map((office: string) => [office])(offices))
    workbook.definedName(area, range)
  }
  const sheet = workbook.sheet('Sheet1')
  for (let i = 0; i < accounts; i++) {
    sheet.cell(`E${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: title.area,
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: title.area,
      operator: 'String',
      formula1: `一覧1!$A$1:$A$${size(areaItems)}`,
      formula2: 'String'
    })
    sheet.cell(`F${i + 2}`).dataValidation({
      type: 'list',
      allowBlank: true,
      showInputMessage: false,
      prompt: false,
      promptTitle: title.office,
      showErrorMessage: true,
      error: 'リストから選択してください',
      errorTitle: title.office,
      operator: 'String',
      formula1: `INDIRECT(E${i + 2})`,
      formula2: 'String'
    })
  }
  // eslint-disable-next-line @typescript-eslint/return-await
  return await workbook.outputAsync({ type: 'uint8array' })
}
export const createSolarAccountsXlsx = async (
  mappings: Array<Record<string, string | null | undefined>>,
  accounts: Array<{
    name: string
    email: string
    group1: string
    group2: string
    status: string
  }>,
  config: ConfigProps
): Promise<Uint8Array | undefined> => {
  const { Accounts, Mappings: configMappings } = config
  const { group } = Accounts
  const { organizations } = configMappings
  const group1 = map((key: string) => group[key])(
    filter((key: string) => key !== 'admin')(keys(group))
  )
  const group2 = map((key: string) => group[key])(
    filter((key: string) => key === 'admin')(keys(group))
  )
  const all = reduce(
    (
      result: Record<
        string,
        Array<
          | string
          | null
          | undefined
          | { key: string | null | undefined; value: string | null | undefined }
        >
      >,
      item: Record<string, string | null | undefined>
    ) => {
      // @ts-expect-error ts(2339)
      map.convert({ cap: false })((key: string, index: number) => {
        if (index === 0) {
          result[key]?.push(item[key])
        } else {
          result[key]?.push({
            key: item[organizations[index - 1]],
            value: item[key]
          })
        }
      })(organizations)
      return result
    },
    reduce(
      (
        sum: Record<
          string,
          Array<
            | string
            | null
            | undefined
            | {
                key: string | null | undefined
                value: string | null | undefined
              }
          >
        >,
        key: string
      ) => ({ ...sum, [key]: [] }),
      {}
    )(organizations)
  )(mappings)
  // const { allArea, allOffices } = reduce(
  //   (
  //     result: {
  //       allArea: string[]
  //       allOffices: Array<{ area: string; office: string }>
  //     },
  //     item: Record<string, string | null | undefined>
  //   ) => {
  //     const { area, office } = item
  //     if (!isNil(area)) {
  //       result.allArea.push(area)
  //       if (!isNil(office)) result.allOffices.push({ area, office })
  //     }
  //     return result
  //   },
  //   { allArea: [], allOffices: [] }
  // )(mappings)
  // @ts-expect-error ts(2339)
  const items = map.convert({ cap: false })((key: string, index: number) => {
    if (index === 0) {
      return sortBy<string>((value) => value)(uniq(all[key]))
    } else {
      return groupBy<{ key: string; value: string }>('key')(all[key])
    }
  })(organizations)
  // @ts-expect-error ts(2339)
  const subItems = map.convert({ cap: false })(
    (
      sub: Record<string, Array<{ key: string; value: string }>>,
      index: number
    ) =>
      map((key: string) => ({
        name: key,
        refersTo: uniq(
          map((item: { key: string; value: string }) => item.value)(sub[key])
        )
      }))(
        index === 0
          ? items[index]
          : sortBy<string>((value) => value)(
              uniq(
                flatten(
                  map((key: string) =>
                    map((items: { key: string; value: string }) => items.value)(
                      items[index][key]
                    )
                  )(keys(items[index]))
                )
              )
            )
      )
  )(slice(1, size(items))(items))
  // const areaItems = sortBy<string>((value) => value)(uniq(allArea))
  // const officeItems = groupBy<{ area: string; office: string }>('area')(
  //   allOffices
  // )
  // const allOfficeItems = map((area: string) => ({
  //   name: area,
  //   refersTo: uniq(
  //     map((item: { area: string; office: string }) => item.office)(
  //       officeItems[area]
  //     )
  //   )
  // }))(areaItems)
  const xlsx = (await json2xlsx(
    map((item: { status: string }) => ({
      ...item,
      status: item.status === 'CONFIRMED' ? '設定済み' : '未設定'
    }))(accounts),
    undefined,
    {
      name: 'アカウント一覧',
      headers: {
        name: '氏名',
        email: 'メールアドレス',
        group1: '役割1',
        group2: '役割2',
        ...reduce(
          (sum: Record<string, string>, key: string) => ({
            ...sum,
            [key]: key
          }),
          {}
        )(organizations),
        status: 'ステータス'
      },
      order: ['name', 'email', 'group1', 'group2', ...organizations, 'status'],
      validations: [
        {
          column: 3,
          title: '役割1',
          list: group1,
          allowBlank: true
        },
        {
          column: 4,
          title: '役割2',
          list: group2,
          allowBlank: true
        },
        // @ts-expect-error ts(2339)
        ...map.convert({ cap: false })((_key: string, index: number) =>
          index === 0
            ? {
                column: 5 + index,
                title: `分類${index + 1}`,
                list: items[0],
                allowBlank: true
              }
            : {
                column: 5 + index,
                title: `分類${index + 1}`,
                indirect: 4 + index,
                allowBlank: true
              }
        )(organizations)
        // {
        //   column: 5,
        //   title: '分類1',
        //   list: items[0],
        //   allowBlank: true
        // },
        // {
        //   column: 6,
        //   title: '分類2',
        //   indirect: 5,1
        //   allowBlank: true
        // }
      ],
      // @ts-expect-error ts(2339)
      definedNames: map.convert({ cap: false })(
        (
          names: Array<{ name: string; refersTo: string[] }>,
          index: number
        ) => ({
          title: `分類${index}`,
          names
        })
      )(subItems)
    }
  )) as Uint8Array | undefined
  return xlsx
}
export const createMappingsXlsx = async (
  mappings: Mapping[],
  accounts: Array<{ name: string }>,
  _ownerAccounts: Array<{ name: string }>,
  config: ConfigProps
): Promise<Uint8Array | undefined> => {
  const { Mappings: configMappings, Mapping: configMapping } = config
  const {
    name: nameKey,
    organizations
  }: { name: string; organizations: string[] } = configMappings
  const maxManagedBy = max([
    max(
      map((item: Mapping) => size(split(',')(item.managedBy)))(mappings)
    ) as number,
    10
  ]) as number
  const headers = {
    name: nameKey,
    address: '住所',
    kind: '種別',
    code: configMappings.code,
    ...reduce(
      (sum: Record<string, string>, key: string) => ({ ...sum, [key]: key }),
      {}
    )(organizations),
    floorArea: '面積（㎡)',
    group: 'グルーピング',
    tenant: 'テナント',
    ...reduce(
      (sum: Record<string, string>, index: number) => ({
        ...sum,
        [`managedBy${index + 1}`]: `担当者${index + 1}`
      }),
      {}
    )(range(0)(maxManagedBy))
  }
  const codeSource = configMapping.fields.code.source
  const order = [
    'name',
    'address',
    'kind',
    codeSource,
    ...organizations,
    'floorArea',
    'group',
    'tenant',
    ...map((index: number) => `managedBy${index + 1}`)(range(0)(maxManagedBy))
  ]
  const validations = [
    {
      column: 9,
      title: `テナント`,
      list: ['○'],
      allowBlank: true,
      endRow: size(mappings) + 2
    },
    ...map((index: number) => ({
      column: 10 + index,
      title: `担当者${index + 1}`,
      list: map((item: { name: string }) => item.name)(accounts),
      allowBlank: true
    }))(range(0)(maxManagedBy))
  ]
  // if (!isNil(configMappings.useOwner) && (configMappings.useOwner as boolean)) {
  //   assign(headers, { ownedBy: 'オーナー' })
  //   order.push('ownedBy')
  //   validations.push({
  //     column: 6,
  //     title: 'オーナー',
  //     list: map((item: { name: string }) => item.name)(ownerAccounts),
  //     allowBlank: true
  //   })
  // }
  const data = map((item: any) => {
    const { managedBy } = item
    const items = split(',')(managedBy)
    return reduce(
      (sum: Record<string, string>, index: number) => ({
        ...sum,
        [`managedBy${index + 1}`]: items[index] ?? ''
      }),
      omit(['managedBy'])(item)
    )(range(0)(maxManagedBy))
  })(mappings)
  const xlsx = (await json2xlsx(
    // !isNil(configMappings.useOwner) && (configMappings.useOwner as boolean)
    //   ? map((item: any) =>
    //       isNil(item['ownedBy']) ? { ...item, ownedBy: '' } : item
    //     )(data)
    //   : map((item: any) => omit(['ownedBy'])(item))(data),
    data,
    undefined,
    {
      name: `${nameKey}一覧`,
      headers,
      order,
      validations,
      transforms: {
        tenant: (value: boolean | null | undefined) =>
          !isNil(value) && value ? '○' : ''
      }
    }
  )) as Uint8Array | undefined
  return xlsx
}
export const createEditDataXlsx = async (
  data: EditData[],
  config: ConfigProps
): Promise<Uint8Array | undefined> => {
  const { Mappings: configMappings } = config
  const { code: codeKey, name: nameKey } = configMappings
  return (await json2xlsx(
    map((item: EditData) => {
      const { user, amounts } = item
      const code = user[codeKey]
      const { name } = user
      return {
        code,
        name,
        ...reduce(
          (
            sum: Record<string, number | string>,
            item: { date: string; daily: number | null }
          ) => ({
            ...sum,
            [format('y年M月')(parseISO(item.date))]: item.daily ?? ''
          }),
          {}
        )(amounts)
      }
    })(data),
    undefined,
    {
      headers: {
        code: codeKey,
        name: nameKey
      }
    }
  )) as Uint8Array | undefined
}
export const doEditDataDownload = async (
  partner: Partial<Partner>
): Promise<{
  data: EditData[] | undefined
}> => {
  if (isNil(url))
    return {
      data: undefined
    }
  const customHeaders = getCustomHeaders({}, partner.id) as
    | AxiosHeaders
    | undefined
  if (isNil(customHeaders))
    return {
      data: undefined
    }
  const headers = {
    Authorization: customHeaders.get('Authorization')
  }
  assign(headers, { 'X-Partner': partner.id })
  const { status, data } = await axios.get(`${url}/editData`, {
    headers
  })
  return status === 200
    ? { data }
    : {
        data: undefined
      }
}
